/* global grecaptcha */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import '../Forms.sass';
import Input from '../Input';
import ErrorLabel from '../ErrorLabel';
import Select from '../Select';
import Radio from '../Radio';
import Checkbox from '../Checkbox';
import CityInput from '../CityInput';
import ButtonPrimary from '../../../uikit/Buttons/Primary';
import SuccessPopup from '../../SuccessPopup';

import {
  CAPTCHA_KEY,
  FORMS_ENDPOINTS,
  VALIDATION_PATTERNS,
} from '../../../utils/constants';
import { constructDevSrc, separateCityCountry } from '../../../utils/helpers';

import IInputs from '../../../interfaces/IInputs';

interface Props {
  nameLabel: string;
  surnameLabel: string;
  emailLabel: string;
  registrationCityLabel: string;
  citySubfieldLabel: string;
  privacyPolicyLabelText: string;
  registrationSkillsLabel: string;
  registrationLevelLabel: string;
  registrationNewslettersAgreementLabel: string;
  registrationPossibleCooperationAgreementLabel: string;
  submitButtonLabel: string;
  cityPlaceholder: string;
  registrationLevelPlaceholder: string;
  registrationSkillsValues: [];
  registrationLevelValues: [];
}

function RegisterEventForm({
   nameLabel,
   surnameLabel,
   emailLabel,
   registrationCityLabel,
   citySubfieldLabel,
   privacyPolicyLabelText,
   registrationSkillsLabel,
   registrationLevelLabel,
   registrationNewslettersAgreementLabel,
   registrationPossibleCooperationAgreementLabel,
   submitButtonLabel,
   cityPlaceholder,
   registrationLevelPlaceholder,
   registrationSkillsValues,
   registrationLevelValues,
 }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    setValue,
    clearErrors,
    watch,
    reset,
  } = useForm<IInputs>({ mode: 'onChange' });

  const watchAllFields = watch();

  const [isFull, setIsFull] = useState<boolean>(false);
  const [isSucceed, setIsSucceed] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  useEffect(() => {
    isSucceed
      ? document.body.classList.add('body-overflow-hidden')
      : document.body.classList.remove('body-overflow-hidden');
  }, [isSucceed]);

  const onClickHandler = () => {
    reset();
    setIsSucceed(false);
    setIsFull(false);
    setIsSending(false);
  };

  useEffect(() => {
    const requiredFields = [
      'name',
      'surname',
      'email',
      // 'city',
      // 'skills',
      // 'expertiseLevel',
      'company',
      'role',
      'privacy',
    ];
    /* @ts-ignore */
    const allTrue = requiredFields.every((field) => {
      /* @ts-ignore */
      if (watchAllFields[field] != false) {
        return true;
      }
      return false;
    });
    if (allTrue && !isSending) {
      setIsFull(true);
    } else {
      setIsFull(false);
    }
  }, [watchAllFields]);
  const onSubmit = async (data: IInputs) => {
    setIsSending(true);
    // const separatedAddress = separateCityCountry(data.city);
    grecaptcha.ready(() => {
      grecaptcha
        .execute(CAPTCHA_KEY, {
          action: 'registerEvent',
        })
        .then((result) => {
          fetch(constructDevSrc(FORMS_ENDPOINTS.registerForm), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              'g-recaptcha-response': result,
              ...data,
              // city: separatedAddress.city,
              // country: separatedAddress.country,
            }),
          })
            .then((response) => {
              if (response.status === 200) {
                setIsSucceed(true);
                setIsSending(false);
              }
            })
            .catch((err) => {
              console.log(err.message);
            });
        });
    });
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-row">
          <div className="form-element form-element--double">
            <Input
              inputName="name"
              label={nameLabel || 'Name'}
              invalid={!!errors.name}
              valid={
                watchAllFields.name !== '' && dirtyFields.name && !errors.name
              }
              required
              register={register}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 2,
                  message:
                    'Minimum required length for this field is 2 characters',
                },
                pattern: {
                  value: VALIDATION_PATTERNS.name,
                  message: 'Entered value should contain characters only',
                },
              }}
            />
            {errors.name && <ErrorLabel message={errors.name.message}/>}
          </div>
          <div className="form-element form-element--double">
            <Input
              inputName="surname"
              label={surnameLabel || 'Surname'}
              invalid={!!errors.surname}
              valid={
                watchAllFields.surname !== '' &&
                dirtyFields.surname &&
                !errors.surname
              }
              required
              register={register}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 2,
                  message:
                    'Minimum required length for this field is 2 characters',
                },
                pattern: {
                  value: VALIDATION_PATTERNS.name,
                  message: 'Entered value should contain characters only',
                },
              }}
            />
            {errors.surname && <ErrorLabel message={errors.surname.message}/>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-element">
            <Input
              inputName="email"
              label={emailLabel || 'E-mail'}
              invalid={!!errors.email}
              valid={
                watchAllFields.email !== '' &&
                dirtyFields.email &&
                !errors.email
              }
              required
              register={register}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 2,
                  message:
                    'Minimum required length for this field is 2 characters',
                },
                pattern: {
                  value: VALIDATION_PATTERNS.email,
                  message: 'Entered value does not match email format',
                },
              }}
            />
            {errors.email && <ErrorLabel message={errors.email.message}/>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-element">
            <Input
              inputName="company"
              label='Company'
              invalid={!!errors.company}
              valid={
                watchAllFields.company !== '' &&
                dirtyFields.company &&
                !errors.company
              }
              required
              register={register}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 2,
                  message:
                    'Minimum required length for this field is 2 characters',
                },
                pattern: {
                  value: VALIDATION_PATTERNS.company,
                  message: 'Entered value is not valid',
                },
              }}
            />
          </div>
          {errors.company && <ErrorLabel message={errors.company.message}/>}
        </div>

        <div className="form-row">
          <div className="form-element">
            <Input
              inputName="role"
              label='Role'
              invalid={!!errors.role}
              valid={
                watchAllFields.role !== '' &&
                dirtyFields.role &&
                !errors.role
              }
              required
              register={register}
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 2,
                  message:
                    'Minimum required length for this field is 2 characters',
                },
                pattern: {
                  value: VALIDATION_PATTERNS.name,
                  message: 'Entered value should contain characters only',
                },
              }}
            />
          </div>
          {errors.role && <ErrorLabel message={errors.role.message}/>}
        </div>

        {/* <div className="form-row"> */}
        {/*  <div className="form-element"> */}
        {/*    <CityInput */}
        {/*      inputName="city" */}
        {/*      required */}
        {/*      register={register} */}
        {/*      invalid={!!errors.city} */}
        {/*      valid={dirtyFields.city && !errors.city} */}
        {/*      label={registrationCityLabel || 'City'} */}
        {/*      subLabel={citySubfieldLabel || ''} */}
        {/*      placeholder={cityPlaceholder} */}
        {/*      isFormSubmitted={isSucceed} */}
        {/*      rules={{ */}
        {/*        required: 'This field is required', */}
        {/*      }} */}
        {/*      setValue={setValue} */}
        {/*      clearErrors={clearErrors} */}
        {/*    /> */}
        {/*    {errors.city && <ErrorLabel message={errors.city.message}/>} */}
        {/*  </div> */}
        {/* </div> */}
        {/* <div className="form-row"> */}
        {/*  <div className="form-element"> */}
        {/*    <Select */}
        {/*      inputName="skills" */}
        {/*      register={register} */}
        {/*      label={registrationSkillsLabel || 'Skills'} */}
        {/*      required */}
        {/*      options={registrationSkillsValues} */}
        {/*      rules={{ */}
        {/*        required: 'This field is required', */}
        {/*      }} */}
        {/*    /> */}
        {/*    {errors.skills && <ErrorLabel message={errors.skills.message}/>} */}
        {/*  </div> */}
        {/* </div> */}
        {/* <div className="form-row"> */}
        {/*  <div className="form-element"> */}
        {/*    <Select */}
        {/*      inputName="expertiseLevel" */}
        {/*      register={register} */}
        {/*      label={registrationLevelLabel || 'Level of expertise'} */}
        {/*      required */}
        {/*      placeholder={registrationLevelPlaceholder || ''} */}
        {/*      options={registrationLevelValues} */}
        {/*      rules={{ */}
        {/*        required: 'This field is required', */}
        {/*      }} */}
        {/*    /> */}
        {/*    {errors.expertiseLevel && ( */}
        {/*      <ErrorLabel message={errors.expertiseLevel.message}/> */}
        {/*    )} */}
        {/*  </div> */}
        {/* </div> */}
        <div className="form-row form-row--gap">
          <div className="form-element">
						<span className="form-label form-label--large">
							{registrationNewslettersAgreementLabel}
						</span>
            <Radio
              defaultChecked
              inputName="newsLettersAgreement"
              label="Yes"
              register={register}
            />
            <Radio
              inputName="newsLettersAgreement"
              label="No"
              register={register}
            />
          </div>
        </div>
        <div className="form-row form-row--gap-md">
          <div className="form-element">
						<span className="form-label form-label--large">
							{registrationPossibleCooperationAgreementLabel}
						</span>
            <Radio
              defaultChecked
              inputName="newsAndColaborationAgreement"
              label="Yes"
              register={register}
            />
            <Radio
              inputName="newsAndColaborationAgreement"
              label="No"
              register={register}
            />
          </div>
        </div>
        <div className="form-row form-row--gap">
          <div className="form-element">
            <Checkbox
              register={register}
              inputName="privacy"
              required
              rules={{ required: 'This field is required' }}
              label={privacyPolicyLabelText}
            />
            {errors.privacy && <ErrorLabel message={errors.privacy.message}/>}
          </div>
        </div>
        <div className="form-row form-row--gap-lg form-row--push-right">
          <ButtonPrimary
            id="submit_register"
            type="submit"
            submitButtonLabel={
              isSending ? 'Sending' : submitButtonLabel || 'Submit'
            }
            disabled={!isFull || Object.keys(errors).length !== 0}
          />
        </div>
      </form>
      {isSucceed && (
        <SuccessPopup
          title="Thank you for registration."
          content="Your seat was successfully reserved. In a few minutes you will receive a registration confirmation email.<br><br>We look forward to seeing you at the IT NonStop conference!"
          closePopup={async () => {
            onClickHandler();
          }}
          isActive={isSucceed}
        />
      )}
    </>
  );
}

export default RegisterEventForm;
